import React, { FC, useCallback, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
	recordHasContractorId,
	compareStrings,
} from '../../utils/DataAccessUtils';
import { Button, Card, Col, Dropdown, Menu, message, Row, Popover } from 'antd';
import { isBuyerOrInternalTech, isInternalTech } from '../../utils/AuthUtils';
import {
	appendWorkOrderNotesForBuyer,
	appendWorkOrderNotesForSupplier,
} from '../../thunks/work_orders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	generateQuotePDF,
	quotesRestCrudThunksForBuyer,
	quotesRestCrudThunksForSupplier,
} from '../../thunks/quotes_thunks';
import QuoteEditButton from '../quote_edit_button/QuoteEditButton';
import ApprovalHierarchyDisplayQuote from '../approval_hierarchy_display_quote/ApprovalHierarchyDisplayQuote';
import CapexSwitch from '../capex_switch/CapexSwitch';
import QuotesAdditionalDetails from '../quotes_components/QuotesAdditionalDetails';
import QuoteUploadButton from '../quote_upload_button/QuoteUploadButton';
import QuotesDetailDisplay from '../quotes_detail_details_page/QuotesDetailDisplay';
import {
	proposalStatusChangeForBuyer,
	proposalStatusChangeForInternalSupplier,
	proposalStatusChangeForSupplier,
	syncProposalApprovalHierarchy,
} from '../../thunks/proposals_thunks';
import {
	markUserNotificationsReadForBuyer,
	markUserNotificationsReadForSupplier,
} from '../../thunks/user_notifications_thunks';
import { BUYER_ONLY_NO_STORE_ASSOCIATE } from '../roles/roles';
import { QUOTE_STATUSES } from '../../constants/QuoteStatuses';
import RequestAdditionalQuotesButton from './RequestAdditionalQuotesButton';
import FileUploader from '../file_uploader/FileUploader';
import {
	getQuoteRestCrudThunksContractorId,
	getQuoteSubURL,
	isClientQuoteView,
} from '../quote_utils/QuoteUtils';
import { Link } from 'react-router-dom';

const style = require('./QuotePageDisplay.less');

const QuotePageDisplay: FC<any> = ({
	quote,
	refreshQuote,
	showViewQuotePage = false,

	currentUser,
	history,
	userType,

	approveQuote,
	declineQuote,
	updateQuote,
	appendWorkOrderNotes,
	syncQuote,

	setAddingMarkup,
	setEditingQuote,
	setCopyQuote,
	isClientView = true,
	otherQuote = {},
}) => {
	const [generatingPDF, setGeneratingPDF] = useState(false);
	const [deletingPDF, setDeletingPDF] = useState(false);

	const hasCreatedQuoteToMyClient = useMemo(() => otherQuote && !!otherQuote.id, [otherQuote]);

	const workOrder = useMemo(() => nullSafeGet('workOrder', quote), [quote]);
	const hasContractorId = useMemo(() => recordHasContractorId(quote), [quote]);
	const currency = useMemo(
		() => getCurrency({ workOrder: nullSafeGet('workOrder', quote) }),
		[quote]
	);

	const generateQuote = useCallback(
		(id) => {
			setGeneratingPDF(true);
			generateQuotePDF(id, userType, currentUser, quote)
				.catch((error) => {
					message.error(error);
				})
				.finally(() => {
					setGeneratingPDF(false);
				});
		},
		[userType, currentUser, quote]
	);

	const handleQuotePDFUploadChange = useCallback(
		(quote) => (quoteFileUrl) => {
			const newQuote = {
				...quote,
				proposalPdfLink: quoteFileUrl,
			};
			if (quoteFileUrl === null) {
				setDeletingPDF(true);
			}
			updateQuote(newQuote)
				.then(() => {
					refreshQuote();
				})
				.catch((error) => {
					message.error(error);
				})
				.finally(() => {
					setDeletingPDF(false);
				});
		},
		[updateQuote, refreshQuote]
	);
	const approvalAllowed = nullSafeGetOrElse(
		'facility.internalTechConfig.allowedToApproveProposal',
		currentUser,
		false
	);
	const isAllowedToApprove =
		userType === ROLE_TYPES.BUYER || (isInternalTech(currentUser) && approvalAllowed);

	const hasPDF = useMemo(() => quote.proposalPdfLink && quote.proposalPdfLink.length > 0, [quote]);
	const hasUploadButton = useMemo(
		() =>
			[QUOTE_STATUSES.disputed, QUOTE_STATUSES.pending].some((status) => status === quote.status),
		[quote]
	);

	const activeRFPAvailable = nullSafeGet('workOrder.rfpDataIds.0', quote);
	const path = getQuoteSubURL(userType, currentUser, quote);

	// @ts-ignore
	return (
		<Row style={{ margin: '16px 8px' }} gutter={12} key={quote.id} className={'QuotePageDisplay'}>
			<Col span={24}>
				<Card>
					<Row justify={'space-between'} style={{ marginBottom: 16 }}>
						<Col />
						<Col>
							<RequestAdditionalQuotesButton workOrder={workOrder} quote={quote} />
							{showViewQuotePage ? (
								<>
									{!activeRFPAvailable ? (
										currentUser.roles.some(
											(role) =>
												BUYER_ONLY_NO_STORE_ASSOCIATE.has(role) || userType === ROLE_TYPES.SUPPLIER
										) ? (
											<Button
												onClick={() => {
													history.push(`/${path}/detail/${quote.id}`);
												}}
											>
												View Quote Page
											</Button>
										) : null
									) : null}
									<QuoteEditButton
										quote={quote}
										style={{ marginLeft: 12 }}
										onClick={() => {
											history.push(
												`/${userType}/workOrders/detail/${quote.workOrder.id}/editProposal${
													hasContractorId ? 'Contractor' : ''
												}/${quote.id}`
											);
										}}
									/>
								</>
							) : null}
						</Col>
					</Row>
					<div
						style={{
							display: 'flex',
							position: 'relative',
							height: 'fit-content',
							flexDirection: 'row',
							marginBottom: 20,
						}}
					>
						<div
							style={{
								position: 'relative',
								flexDirection: 'column',
								width: '100%',
							}}
						>
							<Row className="w-full justify-between align-top">
								{activeRFPAvailable && isBuyerOrInternalTech(currentUser) ? (
									<div className="flex flex-col">
										<div>
											<h5>
												{compareStrings(nullSafeGet('requestForProposal.status', quote), 'award')
													? compareStrings(nullSafeGet('status', quote), QUOTE_STATUSES.disputed)
														? 'This quote was not accepted through the RFP.'
														: compareStrings(nullSafeGet('status', quote), QUOTE_STATUSES.approved)
														? 'This quote was awarded through a RFP.'
														: null
													: 'This quote is currently under review in an RFP.'}
											</h5>
										</div>
										<Link
											to={`/${userType}/${
												isBuyerOrInternalTech(currentUser) ? 'requestsForProposal' : 'opportunities'
											}/detail/${workOrder.rfpDataIds[0]}/details`}
											className="my-2"
										>
											<Button type="primary">View RFP</Button>
										</Link>
									</div>
								) : (
									<ApprovalHierarchyDisplayQuote
										workOrder={workOrder}
										quote={quote}
										currentUser={currentUser}
										approveQuote={approveQuote}
										declineQuote={declineQuote}
										syncQuote={syncQuote}
										appendWorkOrderNotes={appendWorkOrderNotes}
										style={style}
										refresh={refreshQuote}
										history={history}
										allowedToApprove={isAllowedToApprove}
									/>
								)}
								{isBuyerOrInternalTech(currentUser) ? (
									<CapexSwitch
										quote={quote}
										refresh={refreshQuote}
										companyConfig={nullSafeGetOrElse(
											'workOrder.buyerCompany.buyerCompanySettings',
											quote,
											{}
										)}
									/>
								) : isClientView ? null : (
									<div className="flex flex-row justify-end">
										<Popover
											content={
												hasCreatedQuoteToMyClient
													? 'Warning: This will update your existing quote.'
													: null
											}
											placement={'left'}
										>
											<Dropdown
												overlay={
													<Menu>
														<Menu.Item
															onClick={() => {
																setAddingMarkup(true);
																setCopyQuote(quote);
															}}
														>
															Add Markup
														</Menu.Item>
														<Menu.Item
															onClick={() => {
																setEditingQuote(true);
																setCopyQuote(quote);
															}}
														>
															Edit and Copy
														</Menu.Item>
													</Menu>
												}
											>
												<Button style={{ marginRight: 12 }} onClick={(e) => e.preventDefault()}>
													Copy Quote
												</Button>
											</Dropdown>
										</Popover>
									</div>
								)}
							</Row>
						</div>
					</div>
					<QuotesAdditionalDetails
						quote={quote}
						passthroughItems={[
							isBuyerOrInternalTech(currentUser) || hasPDF || hasUploadButton
								? {
										key: 'File',
										value: (
											<QuoteUploadButton
												handleQuotePDFUploadChange={handleQuotePDFUploadChange}
												quote={quote}
												generatingPDF={generatingPDF}
												generateQuotePDF={generateQuote}
												deletingPDF={deletingPDF}
											/>
										),
										className: 'detailRow__item firstRow lastCol',
								  }
								: {},
						]}
					/>
					{nullSafeGet('attachments.0', quote) ? (
						<FileUploader
							roleType={userType}
							defaultFileList={nullSafeGetOrElse('attachments', quote, [])}
							uploaderType={'readOnly'}
						/>
					) : null}
					<QuotesDetailDisplay currency={currency} quote={quote} />
				</Card>
			</Col>
		</Row>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	appendWorkOrderNotes: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? appendWorkOrderNotesForSupplier(entity)
				: appendWorkOrderNotesForBuyer(entity)
		),
	updateQuote: (entity) =>
		dispatch(
			getQuoteRestCrudThunksContractorId(
				ownProps.userType,
				recordHasContractorId(entity),
				isClientQuoteView(entity, ownProps.currentUser)
			).update(entity)
		),
	approveQuote: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? isInternalTech(ownProps.currentUser)
					? proposalStatusChangeForInternalSupplier('awarded', entity)
					: proposalStatusChangeForSupplier('awarded', entity)
				: proposalStatusChangeForBuyer('awarded', entity)
		),
	declineQuote: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? isInternalTech(ownProps.currentUser)
					? proposalStatusChangeForInternalSupplier('declined', entity)
					: proposalStatusChangeForSupplier('declined', entity)
				: proposalStatusChangeForBuyer('declined', entity)
		),
	syncQuote: (entity) => dispatch(syncProposalApprovalHierarchy(ownProps.userType, entity)),
	markUserNotificationsRead: (entityId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? markUserNotificationsReadForSupplier('Quote', entityId)
				: markUserNotificationsReadForBuyer('Quote', entityId)
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(QuotePageDisplay)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		currentUser: (state as any).session.currentUser,
	}),
	() => ({})
)(ComponentWithoutUserType);
